import Field from "./field";
import Entity from "./entity";
import DocumentValue from "./document-value";
import { sortBy } from "../utils";
import IPeriodValue from "../interface/period-value";
import IFieldSortingPrepared from "../interface/field-sorting-prepared";
import IFieldSorting from "../interface/field-sorting";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormula from "../interface/field-cfg-formula";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import SortingValueType from "../enum/sorting-value-type";
import SortingType from "../enum/sorting-type";
import FormulaType from "../enum/formula-type";
import FieldType from "../enum/field-type";

class CollectionSortingInstance {

    /* Public Methods */

    public sort(docA: IDocument, docB: IDocument, sorting: IFieldSortingPrepared[]): number {
        for (let i = 0; i < sorting.length; i++) {
            const result: number = this.compare(docA, docB, sorting[i]);
            if (result) {
                return result;
            }
        }
        return (docA.id as number) - (docB.id as number);
    }

    public prepare(entity: IEntity, sorting: IFieldSorting[]): IFieldSortingPrepared[] {
        sortBy(sorting, 'order');
        const result: IFieldSortingPrepared[] = [];
        sorting.forEach((rule: IFieldSorting) => {
            const field: IField | null = Entity.getField(entity, rule.fieldId);
            if (field) {
                result.push({
                    add: rule.type === SortingType.Ascending ? 1 : -1,
                    type: this.getFieldSortingValueType(field),
                    prefix: DocumentValue.getPrefix(field),
                    fieldId: rule.fieldId
                });
            }
        });
        return result;
    }

    /* Private Public Methods */

    public getNextType(type: SortingType): SortingType | null {
        const sortOrder = [SortingType.Ascending, SortingType.Descending, null];
        return sortOrder[sortOrder.indexOf(type) + 1 % sortOrder.length];
    }

    public getValue(doc: IDocument, sorting: IFieldSortingPrepared): number | string {
        const val: IDocumentValue = doc[`${sorting.prefix}${sorting.fieldId}`];
        return this[sorting.type](val);
    }

    /* Private Methods */

    private compare(a: IDocument, b: IDocument, r: IFieldSortingPrepared): number {
        if (r.type === SortingValueType.text) {
            return ((a[`s${r.fieldId}`] as string || '')).localeCompare(((b[`s${r.fieldId}`] as string) || '')) * r.add;
        } else {
            return ((a[`s${r.fieldId}`] as number) - (b[`s${r.fieldId}`] as number)) * r.add;
        }
    }

    private getFieldSortingValueType(field: IField): SortingValueType {
        if (field.uiType === FieldType.number) {
            return SortingValueType.number;
        } else if (field.uiType === FieldType.datetime) {
            return SortingValueType.datetime;
        } else if (field.uiType === FieldType.formula) {
            const config: IConfigFormula = field.uiConfig as IConfigFormula;
            if (config.type === FormulaType.renewal) {
                const formula: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
                if (formula.showPeriod) {
                    return SortingValueType.period;
                } else {
                    return SortingValueType.datetime;
                }
            } else if (config.type === FormulaType.datetime) {
                return SortingValueType.period;
            } else {
                return SortingValueType.number;
            }
        } else if (field.uiType === FieldType.lookup) {
            if (field.foreignerFCNs.length === 1) {
                const foreignField: IField = Field.get(field.foreignerFCNs[0]) as IField;
                return this.getFieldSortingValueType(foreignField);
            }
        }
        return SortingValueType.text;
    }

    /* Private Methods - Sorting */

    private number(value: IDocumentValue): number {
        return typeof value === 'number' ? value : Global.MIN_NUMBER;
    }

    private datetime(value: IDocumentValue): number {
        if (typeof value === 'string' && value) {
            const val: number = (new Date(value || '')).getTime();
            if (!isNaN(val)) {
                return val;
            }
        }
        return Global.MIN_NUMBER;
    }

    private period(value: IDocumentValue): number {
        const period: IPeriodValue | null = DocumentValue.getPeriod(value);
        if (period) {
            return (period.full as IPeriodValue).s * (period.overdue ? -1 : 1);
        }
        return Global.MIN_NUMBER;
    }

    private text(value: IDocumentValue): string {
        return ((value as string) || '').toLowerCase();
    }

}

const CollectionSorting = new CollectionSortingInstance();

export default CollectionSorting;